// frontend/src/services/tenantService.js
// Service pour vérifier l'existence d'un tenant
import { getTenantSlug } from '../utils/tenant'
import { axiosInstance } from '../utils/apiClient'

/**
 * Vérifie si le tenant actuel existe côté backend
 * Appelle un endpoint léger qui vérifie l'existence de l'organisation
 *
 * @returns {Promise<{exists: boolean, error?: string, errorCode?: string}>}
 */
export async function checkTenantExists() {
  const slug = getTenantSlug()

  // Pas de slug = pas sur un tenant (localhost, app.terrilink.io, etc.)
  if (!slug) {
    return { exists: true, noTenant: true }
  }

  try {
    // Appeler l'endpoint de stats membres (public, ne nécessite pas d'auth)
    // Cet endpoint va déclencher extractTenantFromHost qui vérifie l'existence
    // ✅ Utilise apiClient qui route automatiquement vers le bon domaine tenant
    const response = await axiosInstance.get('/members/stats', {
      headers: {
        'X-Organization-Slug': slug,
      },
      timeout: 10000,
    })

    return { exists: true, data: response.data }
  } catch (error) {
    const errorCode = error.response?.data?.error
    const status = error.response?.status || error.status

    // Organisation renommée (alias d'un slug historique). Le backend
    // renvoie 410 Gone + canonical_origin (réponse applicative de
    // canonicalisation, pas une redirection HTTP au sens 301/308).
    // On reconstruit la cible côté client à partir de l'origin canonique
    // et du pathname/search/hash courants, afin que :
    //   - une navigation utilisateur sur https://old.terrilink.io/feed
    //     redirige bien sur https://new.terrilink.io/feed (et non sur
    //     l'URL API qui a renvoyé le 410)
    //   - querystring + fragment soient préservés
    // Cf. extractTenantFromHost + migration 126 organization_aliases.
    if (status === 410 && errorCode === 'ORGANIZATION_RENAMED') {
      const canonicalOrigin = error.response?.data?.canonical_origin
      if (canonicalOrigin && typeof window !== 'undefined') {
        // Garde anti-boucle : si l'origine actuelle est déjà l'origine
        // canonique, on est dans une configuration d'alias incohérente
        // (alias pointe vers le même hôte que celui d'où vient la requête,
        // ou DNS du canonique manquant). On évite window.location.replace
        // pour ne pas déclencher de boucle de navigation, et on tombe sur
        // le rendu TenantNotFound pour signaler l'incohérence.
        if (canonicalOrigin === window.location.origin) {
          console.error(
            '[TenantService] Boucle d\'alias détectée : ' +
              `canonical_origin (${canonicalOrigin}) === window.location.origin. ` +
              'Configuration alias incohérente côté backend.',
          )
          return {
            exists: false,
            error: 'Configuration alias incohérente',
            errorCode: 'ORGANIZATION_RENAMED',
          }
        }

        const targetUrl =
          canonicalOrigin +
          window.location.pathname +
          window.location.search +
          window.location.hash
        console.info(
          `[TenantService] Slug renommé, redirection vers: ${targetUrl}`,
        )
        window.location.replace(targetUrl)
        // Promesse qui ne se résout jamais : empêche React de rendre quoi que
        // ce soit pendant que le navigateur change d'URL.
        return new Promise(() => {})
      }
      // Fallback si on n'a pas pu lire le canonical_origin ou si on n'est
      // pas dans un navigateur : afficher TenantNotFound avec un code dédié.
      return {
        exists: false,
        error: 'Organisation renommée',
        errorCode: 'ORGANIZATION_RENAMED',
      }
    }

    // Organisation non trouvée
    if (status === 404 && errorCode === 'ORGANIZATION_NOT_FOUND') {
      return {
        exists: false,
        error: 'Organisation introuvable',
        errorCode: 'ORGANIZATION_NOT_FOUND',
      }
    }

    // Organisation supprimée
    if (status === 403 && errorCode === 'TENANT_DELETED') {
      return {
        exists: false,
        error: 'Organisation supprimée',
        errorCode: 'TENANT_DELETED',
      }
    }

    // Organisation suspendue
    if (status === 403 && errorCode === 'TENANT_SUSPENDED') {
      return {
        exists: false,
        error: 'Organisation suspendue',
        errorCode: 'TENANT_SUSPENDED',
      }
    }

    // Abonnement inactif. `neverActivated` distingue un espace qui finit de se
    // provisionner d'un espace réellement résilié — le backend tranche sur le
    // STATUT de la souscription, jamais sur sa présence : une ligne existe déjà
    // pendant le provisionnement.
    // Champ additif : absent d'une réponse d'un backend antérieur → undefined →
    // l'écran retombe sur le message « en pause », comportement d'avant.
    if (status === 403 && errorCode === 'SUBSCRIPTION_INACTIVE') {
      return {
        exists: false,
        error: 'Abonnement inactif',
        errorCode: 'SUBSCRIPTION_INACTIVE',
        neverActivated: error.response?.data?.neverActivated === true,
      }
    }

    // Abonnement expiré
    if (status === 403 && errorCode === 'SUBSCRIPTION_EXPIRED') {
      return {
        exists: false,
        error: 'Abonnement expiré',
        errorCode: 'SUBSCRIPTION_EXPIRED',
      }
    }

    // Base de données tenant introuvable
    if (status === 503 && errorCode === 'TENANT_NOT_FOUND') {
      return {
        exists: false,
        error: 'Base de données introuvable',
        errorCode: 'TENANT_NOT_FOUND',
      }
    }

    // Autres erreurs (réseau, serveur, etc.) - on considère que le tenant existe
    // pour ne pas bloquer l'utilisateur en cas de problème temporaire
    console.warn('[TenantService] Erreur lors de la vérification du tenant:', error.message)
    return { exists: true, checkFailed: true }
  }
}

export default {
  checkTenantExists,
}
